import { z } from 'zod';
import { MICRONUTRIENT_CODES } from '../nutrition/micronutrient-registry';
import { COMPOSITION_NAME_ROLES, COMPOSITION_ROLES } from './composition-roles-lexicon';
import { PREPACKAGED_LABEL_EDITOR_MODES } from './editor-mode';
import { IdentifierSchema } from './identifier.schema';
import {
  PREPACKAGED_NOMINAL_RANGE_CATEGORIES,
  type PrepackagedNominalRangeCategory,
} from './nominal-quantity-ranges';
import { PACKAGING_DISPOSAL_STREAMS } from './packaging-material-catalog';
import {
  isPrepackagedMarketCountryCode,
  PREPACKAGED_MARKET_COUNTRY_CODES,
  type PrepackagedMarketCountryCode,
  PrepackagedMarketCountryCodeSchema,
} from './prepackaged-country-names';
import { IDENTIFICATION_MARK_MEMBER_STATE_CODES } from './prepackaged-identification-mark';
import { PREPACKAGED_VERTICAL_ORIGIN_REGIMES } from './prepackaged-vertical-origin';

/**
 * Re-exported so every existing importer of this module — the browser
 * bundle's country autocompletes above all — keeps working unchanged. The
 * catalogue, its type and its guard live in `prepackaged-country-names.ts`,
 * a deliberate leaf module neither this file nor `prepackaged-vertical-origin.ts`
 * may import FROM the other through: see that file's top comment for the
 * import-cycle incident this re-export exists to prevent from recurring.
 */
export {
  isPrepackagedMarketCountryCode,
  PREPACKAGED_MARKET_COUNTRY_CODES,
  type PrepackagedMarketCountryCode,
  PrepackagedMarketCountryCodeSchema,
};

/**
 * Identity sent by legacy label clients when they ask the server to preview or
 * freeze a lot. Keep this value next to the shared regulatory contract so the
 * backend registry and browser bundle cannot drift into independent literals.
 */
/**
 * V4 (agosto 2026) adds one rule — art. 18 §3, engineered nanomaterials, which
 * only the operator can answer — and changes how five others decide:
 * storage conditions became unconditional for a use-by date (All. X, punto 2,
 * lett. b); the operator address is checked for reachability (art. 9 §1
 * lett. h); a claim found in free text reopens the declaration (Reg. CE
 * 1924/2006); an origin whose primary ingredient differs stops for specialist
 * review (art. 26 §3, Reg. UE 2018/775); and the packaging-regulation
 * transition follows a date instead of a constant; and the QUID percentage is
 * checked for sitting next to the denomination or the ingredient list
 * (All. VIII, punto 3, lett. b) rather than merely existing. A frozen
 * evaluation keeps the version and hash it was produced under, so an
 * evaluation from V3 stays readable exactly as it was decided.
 *
 * V6 (cluster C, termini riservati e logo biologico UE, G4/G7) adds four
 * rules: which reserved regime terms (biologico/bio/eco, DOP/IGP/STG, senza
 * glutine) a free text claims and whether the operator holds the title that
 * authorises it (art. 30 Reg. (UE) 2018/848), what the biologico logo block
 * must carry once the operator is certified (art. 32 Reg. (UE) 2018/848),
 * the analytical evidence a gluten claim needs (Reg. di esecuzione (UE)
 * 828/2014) and the specialist routing for a declared geographical
 * indication (Reg. (UE) 2024/1143). It also widens `supportedProfile` to
 * accept `ORGANIC` as a modelled special category — a certified operator can
 * now reach a printable label instead of being refused outright.
 *
 * V7 (cluster F, quantità netta e metrologia legale, G10/G11/G12) adds eight
 * rules: the Allegato IX punto 1 omissioni della quantità netta (perdite
 * notevoli, sotto 5 g/ml, vendita a pezzo contabile), le due indicazioni del
 * preimballaggio multiplo (Allegato IX punti 3-4), la conferma che il peso
 * dichiarato di un alimento glassato è già al netto della glassa (Allegato
 * IX punto 5), le gamme nominali residue per vini e bevande spiritose (dir.
 * 2007/45/CE), l'altezza minima dei caratteri della quantità nominale (dir.
 * 76/211/CEE All. I punto 3.1), i presupposti verificabili del marchio «℮»
 * e la scadenza della verificazione periodica dello strumento di pesatura
 * (D.M. 93/2017), e l'errore massimo tollerato in difetto come avviso
 * informativo mai bloccante (dir. 76/211/CEE All. I punto 2.4).
 */
// V8 / cluster E fase B: tre regole nuove — All. VI Parte A punto 2
// («decongelato», derivato da `Recipe.servedDefrosted`, mai chiesto),
// All. VI Parte A punto 3 (radiazioni ionizzanti, domanda di profilo con
// dicitura stampata deterministica, mai `REQUIRES_SPECIALIST_REVIEW`),
// All. VII Parte A punti 8-9 (origine vegetale specifica di oli/grassi,
// bloccante e azionabile).
// V9 / cluster E fase C: chiude la tranche lasciata aperta in fase B — All.
// VII Parte B punto 17 (designazione generica delle carni). Il catalogo dei
// tenori (`annex-vii-designations.ts`, `GENERIC_MEAT_CATEGORY_THRESHOLDS`)
// era già completo; questa fase aggiunge la lettura del tenore reale di
// grassi/collagene per ingrediente (`PrepackagedLabelMeatCompositionRepository`,
// nuova colonna `IngredientNutritionalProfile.collagenToMeatProteinRatioPercent`)
// — mai `REQUIRES_SPECIALIST_REVIEW` a tempo indeterminato.
//
// V10 / cluster D fase B: undici regole nuove, una per regime verticale
// dell'origine più la regola di scope che raccoglie la decisione
// dell'operatore — `characteristics.verticalOriginRegimes`, closed catalogue
// in `prepackaged-vertical-origin.ts` (fase A, già verde). Riconciliazione
// obbligatoria: `EU.1169.ART26.ORIGIN` non decide più da sola l'obbligo di
// origine delle carni fresche (Allegato XI) — quando l'operatore ha
// dichiarato `BEEF` o `OTHER_MEAT_FRESH`, ART26 si mette da parte
// (`NOT_REQUIRED`) e lascia decidere `EU.1760.ART13.BEEF_ORIGIN` /
// `EU.1337.ART5.MEAT_ORIGIN`, che leggono lo stesso campo con lo stesso
// predicato condiviso (`verticalOriginCoversMeatObligation`), mai una
// seconda derivazione. `REQUIRES_SPECIALIST_REVIEW` resta raggiungibile solo
// per `OTHER_REQUIRES_REVIEW` (input non riconosciuto, decisione del
// proprietario) e per la scadenza dei cinque regimi sperimentali italiani.
// V12 (2026-08-22): la regola di scope IT.SCOPE.VERTICAL_ORIGIN_REGIME esce
// dal rule pack e i dieci regimi verticali puntano alle schede ingrediente
// (`RECIPE_INGREDIENTS`): i dati di origine verticale si raccogliono solo sulla
// scheda ingrediente, il form etichetta non li chiede più. I regimi si deducono
// dagli entry `verticalOrigin` degli ingredienti (`facts.verticalOrigin`, mai
// più dalla domanda profilo). V10 / cluster D fase B: undici regole nuove, una
// per regime verticale dell'origine più la regola di scope che raccoglieva la
// decisione dell'operatore — riconciliazione obbligatoria: EU.1169.ART26.ORIGIN
// non decide da sola l'obbligo di origine delle carni fresche (Allegato XI) —
// quando un ingrediente dichiara `BEEF` o `OTHER_MEAT_FRESH`, ART26 si mette da
// parte (`NOT_REQUIRED`) e lascia decidere `EU.1760.ART13.BEEF_ORIGIN` /
// `EU.1337.ART5.MEAT_ORIGIN`, che leggono gli stessi entry con lo stesso
// predicato condiviso (`verticalOriginCoversMeatObligation`), mai una seconda
// derivazione. `REQUIRES_SPECIALIST_REVIEW` resta raggiungibile solo per
// `OTHER_REQUIRES_REVIEW` (input non riconosciuto, decisione del proprietario,
// ora segnalata dalla sola ART26) e per la scadenza dei cinque regimi
// sperimentali italiani.
// V13 (2026-08-24): chiude il cluster E (G8 tranche B/C e G9 Parte D) con sei
// regole nuove — All. VI Parte A punti 4 (componente sostituito, campo di
// progetto `substituteIndication`), 5 (proteine animali aggiunte di diversa
// origine, `characteristics.addedAnimalProteinOrigin`), 6 (acqua aggiunta
// oltre il 5% su carni/pesca a taglio, `characteristics.meatFisheryCutPresentation`
// + fatto derivato `addedWaterPercentOfFinished`), 7 (prodotti formati da più
// parti, `characteristics.formedFromMultipleParts`), Parte C (budello non
// commestibile, `characteristics.nonEdibleCasing`) e All. VII Parte D punto 3
// (chinina/caffeina come aromi, `characteristics.flavouringWithQuinineOrCaffeine`
// + fatto `aromaRowsLackingNamedStimulant`). Nessuna delle cinque domande è
// tri-stato bloccante: tutte a ambito ristretto (carne/pesce o aromi con
// stimolanti), null significa «non si applica» — stesso pattern dei regimi
// verticali del cluster D, mai `NEEDS_ANSWER` su profili salvati prima.
// V14 (2026-09): il pack diventa un documento (label-rule-pack.seed.ts); l'hash
// sigilla il documento intero, non più i soli metadati del registry.
// V15 (2026-09): EU.1169.ART27.USAGE portata a DSL, EU.1169.ART24.DATE_DECISION
// torna nativa (letta da DATE_VALUE/LOT_VALUE), allowsSpecialistReview per
// regola. Ogni modifica al seed bumpa la versione: a parità di packVersion il
// provider adotta la riga pubblicata e il drift resterebbe invisibile.
// V16 (2026-09): productKind, preset PASTRY, R0-R8 (spec fase 2
// 2026-09-05-label-rule-engine-phase2-pastry).
// V17 (2026-09): `characteristics.hasAddedAnimalProtein` — la domanda Sì/No
// che apre l'origine della proteina animale aggiunta (All. VI Parte A p.5).
// V18 (2026-09): «Servono istruzioni di conservazione?» non si pone sotto
// data «da consumarsi entro» (All. X punto 2 lett. b, obbligo incondizionato).
// V19 (2026-10): campo di progetto `includeTenantContamination` nella sezione
// «Aggiuntive» — opt-in per includere nel «può contenere» le categorie di
// contaminazione dichiarate dal laboratorio (spento di default).
// V20 (2026-10, DEV-283): fatto `missingIngredients[].missingFact` — quale dato
// della scheda (densità, peso per pezzo, valori, parte edibile) blocca la
// dichiarazione nutrizionale; il vocabolario dei fatti è nel sigillo.
export const IT_PREPACKAGED_RULE_PACK_VERSION = 'IT_PREPACKAGED_2026_10_V20';
export const IT_PREPACKAGED_RULE_PACK_HASH =
  '5e4a16cd320971a4a8ca228a7761c059fc4c988ada0e7441d07d946ac5cf1e1b';
export const IT_PREPACKAGED_RULE_PACK_IDENTITY = {
  rulePackVersion: IT_PREPACKAGED_RULE_PACK_VERSION,
  rulePackHash: IT_PREPACKAGED_RULE_PACK_HASH,
} as const;
export type PrepackagedRulePackIdentity = {
  rulePackVersion: string;
  rulePackHash: string;
};

export const PREPACKAGED_CHECKLIST_STATUSES = [
  'TO_COMPLETE',
  'NEEDS_ANSWER',
  'READY',
  'NOT_REQUIRED',
  'REQUIRES_SPECIALIST_REVIEW',
  'SUGGESTION',
] as const;

export const PREPACKAGED_SALES_MODES = [
  'FULL_PREPACKAGED',
  'DIRECT_SALE_PREPACKAGED',
  'PACKED_ON_REQUEST',
  'LOOSE',
  'CATERING',
  'INTERNAL_HACCP',
] as const;

export const PREPACKAGED_SPECIAL_CATEGORIES = [
  'FISHERY',
  'MEAT',
  'EGGS',
  'HONEY',
  'ORGANIC',
  'GEOGRAPHICAL_INDICATION',
  'FOOD_FOR_SPECIAL_GROUPS',
] as const;

/** Fase 2 (spec §3.1): il tipo di prodotto si dichiara all'apertura
 *  dell'etichetta; solo `GENERIC` e `PASTRY` sono attivi, gli altri compaiono
 *  come «prossimamente» e una riga che li porta via API torna alla domanda
 *  (`IT.SCOPE.PRODUCT_KIND`). */
export const PREPACKAGED_PRODUCT_KINDS = [
  'GENERIC',
  'PASTRY',
  'GASTRONOMY',
  'BAKERY',
  'ICE_CREAM',
] as const;
export type PrepackagedProductKind = (typeof PREPACKAGED_PRODUCT_KINDS)[number];
/** Fase 2 (spec §3.1): «surgelato» è denominazione riservata (D.Lgs. 110/1992
 *  artt. 2-3), «congelato» è lo stato fisico dell'All. VI Parte A p.1,
 *  «decongelato» il p.2. */
export const PREPACKAGED_THERMAL_STATES = [
  'QUICK_FROZEN',
  'FROZEN',
  'DEFROSTED',
  'NEVER_FROZEN',
] as const;
export type PrepackagedThermalState = (typeof PREPACKAGED_THERMAL_STATES)[number];
/** Reg. (CE) 1334/2008 art. 16 §2, §4, §6: i tre stati dell'aroma di vaniglia. */
export const PREPACKAGED_VANILLA_FLAVOURING_KINDS = [
  'SYNTHETIC',
  'NATURAL_OTHER',
  'NATURAL_VANILLA',
] as const;
export type PrepackagedVanillaFlavouringKind =
  (typeof PREPACKAGED_VANILLA_FLAVOURING_KINDS)[number];

export const PREPACKAGED_SALES_CHANNELS = ['PHYSICAL', 'DISTANCE'] as const;
export const PREPACKAGED_SHELF_LIFE_DECISIONS = ['BEST_BEFORE', 'USE_BY', 'EXEMPT'] as const;
export const PREPACKAGED_LOT_IDENTIFICATION_DECISIONS = [
  'LOT_CODE',
  'DATE_WITH_DAY_MONTH',
] as const;
export const PREPACKAGED_ORIGIN_PRESENTATIONS = [
  'NONE',
  'VOLUNTARY_SAME_AS_PRIMARY',
  'VOLUNTARY_DIFFERENT_PRIMARY',
  'MANDATORY',
  /**
   * Art. 26 §3 Reg. (UE) 1169/2011 + Reg. di esecuzione (UE) 2018/775: la
   * verifica sull'origine dell'ingrediente primario si applica anche quando
   * l'indicazione d'origine è obbligatoria, non solo quando è volontaria —
   * `MANDATORY` da solo presumeva silenziosamente che i due coincidessero.
   */
  'MANDATORY_DIFFERENT_PRIMARY',
] as const;
export const PREPACKAGED_PRIMARY_INGREDIENT_ORIGIN_FORMS = [
  'SPECIFIC',
  'STANDARD_SINGULAR',
  'STANDARD_PLURAL',
] as const;
export type PrepackagedPrimaryIngredientOriginForm =
  (typeof PREPACKAGED_PRIMARY_INGREDIENT_ORIGIN_FORMS)[number];
export const PREPACKAGED_NUTRITION_EXEMPTIONS = [
  'NONE',
  'UNPROCESSED_SINGLE_INGREDIENT',
  'SMALL_PACKAGE_UNDER_25_CM2',
  'SMALL_DIRECT_SUPPLY',
  'ALCOHOL_OVER_1_2',
  'OTHER_REQUIRES_REVIEW',
] as const;
/**
 * Numeric thresholds that gate whether an obligation applies at all, kept
 * here — once, named, with the article that sets them — so the backend
 * evaluator/adapter and their tests read the same number instead of each
 * burying its own copy of the literal. A literal repeated in the engine and
 * in the test that is supposed to guard it proves nothing: the day the norm
 * changes, the test still passes by comparing the old number to itself.
 */

/**
 * Art. 16 §3 lett. e) + Allegato V punto 18 Reg. (UE) 1169/2011: sotto i
 * 25 cm² di superficie della faccia più grande, la dichiarazione nutrizionale
 * non è obbligatoria. La soglia è ESCLUSIVA — l'esenzione vale solo per una
 * superficie strettamente minore di 25 cm²; a 25 cm² esatti l'obbligo torna
 * pieno.
 */
export const SMALL_PACKAGE_NUTRITION_EXEMPTION_CEILING_CM2 = 25;

/**
 * Art. 16 §2 Reg. (UE) 1169/2011: sotto i 10 cm² di superficie della faccia
 * più grande, l'elenco degli ingredienti non è obbligatorio. Soglia
 * ESCLUSIVA come sopra — a 10 cm² esatti l'obbligo torna pieno.
 */
export const INGREDIENTS_LIST_EXEMPTION_CEILING_CM2 = 10;

/**
 * Art. 13 §3 Reg. (UE) 1169/2011: sotto gli 80 cm² di superficie della faccia
 * più grande si applica la deroga sull'altezza minima dei caratteri
 * (x-height). Soglia ESCLUSIVA — a 80 cm² esatti la deroga non si applica
 * più.
 */
export const SMALL_PACKAGE_X_HEIGHT_DEROGATION_CEILING_CM2 = 80;

/**
 * Art. 28 §2 lett. a) Reg. (UE) 1169/2011: la dichiarazione del titolo
 * alcolometrico è obbligatoria per le bevande con più dell'1,2% di alcol in
 * volume. La soglia è ESCLUSIVA — a 1,2% esatti l'obbligo non scatta ancora.
 */
export const ALCOHOL_DECLARATION_THRESHOLD_ABV_PERCENT = 1.2;

export const PREPACKAGED_ANNEX_III_TRIGGERS = [
  'PROTECTIVE_ATMOSPHERE',
  'SWEETENERS',
  'SWEETENERS_AND_SUGARS',
  'ASPARTAME',
  'CAFFEINE',
  'LIQUORICE',
  'POLYOLS',
  'PHYTOSTEROLS',
  'FROZEN_MEAT_OR_FISH_DATE',
] as const;

/**
 * `HIGH_CAFFEINE` e `LICORICE_GLYCYRRHIZIN` sono stati accorpati ai trigger
 * automatici `CAFFEINE`/`LIQUORICE` (stesso punto dell'Allegato III, stesso
 * esito: senza dati quantitativi l'evaluator arriva comunque alla revisione
 * specialista). I profili salvati prima dell'accorpamento vengono ripuliti in
 * lettura e in scrittura da `normalizeLegacyAnnexIIITriggers` — non esiste una
 * migrazione sulle righe, quindi il mapping resta finché esistono profili
 * vecchi.
 */
const LEGACY_ANNEX_III_TRIGGER_ALIASES: Record<
  string,
  (typeof PREPACKAGED_ANNEX_III_TRIGGERS)[number]
> = {
  HIGH_CAFFEINE: 'CAFFEINE',
  LICORICE_GLYCYRRHIZIN: 'LIQUORICE',
};

export function normalizeLegacyAnnexIIITriggers(value: unknown): unknown {
  if (!Array.isArray(value)) {
    return value;
  }
  const mapped = value.map((trigger) =>
    typeof trigger === 'string' ? (LEGACY_ANNEX_III_TRIGGER_ALIASES[trigger] ?? trigger) : trigger,
  );
  return Array.from(new Set(mapped));
}

export const PREPACKAGED_PROFILE_FIELD_PATHS = [
  'marketCountries',
  'firstMarketDate',
  'salesMode',
  'channels',
  'specialCategories',
  // Fase 2 (spec §3.1): il tipo di prodotto è la prima domanda di Scenario.
  'productKind',
  'package.largestSurfaceCm2',
  'package.largestSideAreaCm2',
  'package.inLiquidMedium',
  'package.separableComponents',
  'characteristics.shelfLife',
  'characteristics.shelfLifeDurationMonths',
  'characteristics.lotIdentification',
  'characteristics.storageInstructionsNeeded',
  'characteristics.usageInstructionsNeeded',
  'characteristics.originPresentation',
  'characteristics.emphasizedIngredientNames',
  'characteristics.alcoholicBeverage',
  'characteristics.alcoholByVolume',
  'characteristics.nutritionExemption',
  'characteristics.hasClaims',
  'characteristics.containsEngineeredNanomaterials',
  'characteristics.treatedWithIonisingRadiation',
  'characteristics.meatFisheryCutPresentation',
  'characteristics.formedFromMultipleParts',
  'characteristics.hasAddedAnimalProtein',
  'characteristics.addedAnimalProteinOrigin',
  'characteristics.nonEdibleCasing',
  'characteristics.flavouringWithQuinineOrCaffeine',
  'characteristics.annexIIITriggers',
  'characteristics.annexIIICaffeineMgPerL',
  'characteristics.annexIIICaffeineMatrix',
  // `characteristics.annexIIILiquoricePresent` non è più una domanda: la
  // presenza È la checkbox LIQUORICE di `annexIIITriggers`. Il campo resta
  // nello schema (i profili legacy continuano a parsare) ma non ha più né
  // editor né target, quindi non compare in questa lista.
  'characteristics.annexIIILiquoriceAmount',
  'characteristics.annexIIILiquoriceUnit',
  'characteristics.annexIIILiquoriceMatrix',
  'characteristics.annexIIILiquoriceAlcoholByVolume',
  'characteristics.annexIIIAspartameDesignation',
  'characteristics.animalOriginApprovedEstablishment',
  'characteristics.manufacturingCountry',
  'characteristics.organicCertificationHeld',
  'characteristics.organicScope',
  'characteristics.glutenClaimEvidence',
  // F / G10-G12.
  'package.multipack',
  'package.multipackPackageCountVisibleFromOutside',
  'package.multipackUnitNetQuantityVisibleFromOutside',
  'package.glazed',
  'characteristics.netQuantityExemption',
  'characteristics.netQuantityExemptionIsSpiceOrHerb',
  'characteristics.netQuantityPieceCountVisibleFromOutside',
  'characteristics.nominalRangeCategory',
  // D / G6.
  'characteristics.verticalOriginRegimes',
  // Fase 2 (spec §3.1): i quattro campi pasticceria.
  'characteristics.thermalState',
  'characteristics.vanillaFlavouringKind',
  'characteristics.artisanRegistered',
  'characteristics.reservedBakeryMinimaVerified',
] as const;

/**
 * Art. 4 D.Lgs. 145/2017. Non esiste un'opzione «codice ministeriale»: il
 * decreto non la prevede (verifica su Normattiva, art. 4 c. 4). Non esiste
 * neppure la quarta opzione dell'art. 4 c. 3 lett. c) — «il marchio
 * d'impresa contiene gia' la sede» — decisione del proprietario del prodotto
 * del 19 agosto 2026: sarebbe una scorciatoia a un clic senza che nessuno
 * verifichi il presupposto.
 */
export const PREPACKAGED_ESTABLISHMENT_INDICATIONS = [
  'SAME_AS_OPERATOR', // art. 4 c. 3 lett. a): coincide con la sede dell'operatore gia' stampata
  'OWN_ADDRESS', // art. 4 c. 1: localita' e indirizzo dello stabilimento
  'MULTIPLE_PLANTS', // art. 4 c. 4: tutti gli stabilimenti, quello effettivo punzonato
] as const;
export type PrepackagedEstablishmentIndication =
  (typeof PREPACKAGED_ESTABLISHMENT_INDICATIONS)[number];

/**
 * Art. 7 D.Lgs. 145/2017 (mutuo riconoscimento): il decreto non si applica ai
 * preimballati legalmente fabbricati o commercializzati in un altro Stato
 * membro, in Turchia, o fabbricati in uno Stato EFTA che fa parte del SEE
 * (Islanda, Liechtenstein, Norvegia — la Svizzera e' EFTA ma non SEE, quindi
 * esclusa). Sede di stampa: `IT.145.ART3.ESTABLISHMENT` nell'evaluator.
 */
export const PREPACKAGED_MUTUAL_RECOGNITION_EXEMPT_COUNTRIES = [
  ...IDENTIFICATION_MARK_MEMBER_STATE_CODES.filter((code) => code !== 'IT'),
  'TR',
  'IS',
  'LI',
  'NO',
] as const;

export const PREPACKAGED_NET_QUANTITY_UNITS = ['G', 'KG', 'ML', 'CL', 'L'] as const;
export type PrepackagedNetQuantityUnit = (typeof PREPACKAGED_NET_QUANTITY_UNITS)[number];

/**
 * G13 (`docs/plans/prepackaged-G-lingua-ux-pulizia.md`, corretto dall'errata
 * 2026-08-20 § 2.1): il mercato di destinazione decide la lingua, art. 15
 * §§1-3 Reg. (UE) 1169/2011
 * (https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:32011R1169) —
 * non l'operatore, non l'interfaccia. Decisione del proprietario: **solo
 * `it`**. Il tedesco è stato tolto dal perimetro in verifica: non esiste un
 * obbligo di bilinguismo a Bolzano (il D.P.R. 574/1988 copre la pubblica
 * amministrazione, non l'etichettatura alimentare) e non esiste un catalogo
 * di diciture verificato per una seconda lingua. Una lingua ulteriore si apre
 * quando esiste, per quel mercato, il catalogo verificato — non prima, e mai
 * tradotta a mano da un agente. L'insieme resta qui, e non un letterale
 * `'it'` sparso nel composer, perché il payload congelato (`layout.
 * marketLanguages`, V6) deve poter dimostrare in quale lingua l'artefatto fu
 * stampato anche quando quella lingua è, oggi, sempre la stessa.
 */
export const PREPACKAGED_LABEL_LANGUAGES = ['it'] as const;
export type PrepackagedLabelLanguage = (typeof PREPACKAGED_LABEL_LANGUAGES)[number];

/**
 * G15 (`docs/plans/prepackaged-G-lingua-ux-pulizia.md`): art. 30 §3 Reg. (UE)
 * 1169/2011 ammette solo due forme di ripetizione nutrizionale nel campo
 * visivo principale — il solo valore energetico, oppure il valore energetico
 * accompagnato da grassi, acidi grassi saturi, zuccheri e sale. Un enum
 * chiuso, non un elenco libero di nutrienti: un elenco libero permetterebbe
 * di comporre una terza forma che l'articolo non ammette.
 */
export const PREPACKAGED_FRONT_OF_PACK_REPETITIONS = [
  'NONE',
  'ENERGY_ONLY',
  'ENERGY_AND_FOUR',
] as const;
export type PrepackagedFrontOfPackRepetition =
  (typeof PREPACKAGED_FRONT_OF_PACK_REPETITIONS)[number];

/**
 * All. IX punti 3-4 Reg. (UE) 1169/2011: come si presenta il numero che
 * l'operatore digita. `SINGLE` = un solo preimballaggio, il numero è la
 * quantità netta. `MULTIPACK_PER_UNIT` (punto 3) = due o più preimballaggi
 * individuali, STESSA quantità, unità di vendita: il numero è quello DI
 * CIASCUNA unità. `MULTIPACK_TOTAL` (punto 4) = due o più imballaggi
 * individuali che NON sono unità di vendita: il numero è il TOTALE.
 * Canonica qui — non in `net-quantity-declaration.ts` né nel payload V6 —
 * perché è la SSOT unica per i tre punti che altrimenti la dichiaravano
 * ciascuno per conto proprio (errata cluster F § 3): `net-quantity-declaration.ts`
 * la re-esporta, `backend/.../prepackaged-label.types.ts` la importa da qui.
 */
export const PREPACKAGED_NET_QUANTITY_PRESENTATIONS = [
  'SINGLE',
  'MULTIPACK_PER_UNIT',
  'MULTIPACK_TOTAL',
] as const;
export type PrepackagedNetQuantityPresentation =
  (typeof PREPACKAGED_NET_QUANTITY_PRESENTATIONS)[number];

/** All. IX punto 1 Reg. (UE) 1169/2011: le tre omissioni ammesse dell'indicazione della quantità netta. */
export const PREPACKAGED_NET_QUANTITY_EXEMPTION_REASONS = [
  'NONE',
  'SIGNIFICANT_LOSSES', // lett. a)
  'UNDER_5_G_OR_ML', // lett. b), salvo spezie ed erbe aromatiche
  'SOLD_BY_PIECE_COUNTABLE', // lett. c)
] as const;
export type PrepackagedNetQuantityExemptionReason =
  (typeof PREPACKAGED_NET_QUANTITY_EXEMPTION_REASONS)[number];

/**
 * All. IX punti 3-4: se il preimballaggio è composto da più unità e, se sì,
 * se quelle unità sono a loro volta unità di vendita (punto 3, stessa
 * quantità, stesso prodotto) o non lo sono (punto 4).
 */
export const PREPACKAGED_MULTIPACK_KINDS = [
  'NONE',
  'UNITS_ARE_SALES_UNITS',
  'UNITS_ARE_NOT_SALES_UNITS',
] as const;
export type PrepackagedMultipackKind = (typeof PREPACKAGED_MULTIPACK_KINDS)[number];

/** Dir. 76/211/CEE All. I punto 3.3: chi garantisce la conformità del preimballaggio. */
export const PREPACKAGED_EMARK_ROLES = ['PACKER', 'IMPORTER'] as const;
export type PrepackagedEMarkRole = (typeof PREPACKAGED_EMARK_ROLES)[number];

/**
 * D.M. 21 aprile 2017 n. 93, Allegato IV: solo le tipologie con cui si
 * determina la massa di un preimballaggio entrano nel modulo (le altre righe
 * dell'Allegato — misuratori di liquidi, gas metano, misure di capacità —
 * non riguardano un preimballaggio pesato).
 */
export const PREPACKAGED_WEIGHING_INSTRUMENT_KINDS = [
  'NON_AUTOMATIC', // NAWI, periodicità 3 anni
  'AUTOMATIC_CHECKWEIGHER', // selezionatrice ponderale, periodicità 1 anno
  'AUTOMATIC_PRICE_LABELLER', // etichettatrice di peso/prezzo, periodicità 1 anno
  'AUTOMATIC_OTHER', // altri strumenti a funzionamento automatico, periodicità 2 anni
] as const;
export type PrepackagedWeighingInstrumentKind =
  (typeof PREPACKAGED_WEIGHING_INSTRUMENT_KINDS)[number];

/**
 * Art. 32 §2 Reg. (UE) 2018/848: dove sono state coltivate le materie prime
 * agricole, nello stesso campo visivo del logo. `COUNTRY` è ammesso solo
 * quando TUTTE le materie prime agricole provengono da quel paese (secondo
 * comma); il nome del paese viaggia separatamente in
 * `organicAgricultureOriginCountry` perché non è un'enumerazione chiusa.
 */
export const PREPACKAGED_ORGANIC_ORIGINS = ['EU', 'NON_EU', 'EU_NON_EU', 'COUNTRY'] as const;
export type PrepackagedOrganicOrigin = (typeof PREPACKAGED_ORGANIC_ORIGINS)[number];

/**
 * All. V punto 2 Reg. (UE) 2018/848: «AB-CDE-999» — codice ISO del paese di
 * controllo, tre lettere che richiamano il biologico, fino a tre cifre. La
 * seconda alternativa copre gli organismi della provincia autonoma di
 * Bolzano, che l'elenco MASAF pubblica senza trattini e con suffisso
 * provinciale (`ITBIO001BZ`). Il modulo verifica solo la FORMA: non esiste un
 * registro interrogabile a runtime, come già per l'indirizzo dell'OSA.
 */
export const ORGANIC_CONTROL_BODY_CODE_PATTERN =
  /^(?:[A-Z]{2}-[A-Z]{3}-\d{1,3}|[A-Z]{2}[A-Z]{3}\d{3}[A-Z]{2})$/;

/**
 * Art. 30 §5 Reg. (UE) 2018/848: `PROCESSED_95` è la lett. a) (≥95%
 * ingredienti agricoli biologici in peso, termine in denominazione, logo
 * obbligatorio); `INGREDIENTS_ONLY` copre le lett. b) e c) (termine solo
 * nell'elenco ingredienti, logo vietato); `IN_CONVERSION` è l'art. 30 §3.
 */
export const PREPACKAGED_ORGANIC_SCOPES = [
  'PROCESSED_95',
  'INGREDIENTS_ONLY',
  'IN_CONVERSION',
] as const;
export type PrepackagedOrganicScope = (typeof PREPACKAGED_ORGANIC_SCOPES)[number];

/** Allegato, sezione A, Reg. di esecuzione (UE) 828/2014: le due soglie
 *  analitiche, più "non misurato". */
export const PREPACKAGED_GLUTEN_CLAIM_EVIDENCES = [
  'NONE',
  'BELOW_20_MGKG',
  'BELOW_100_MGKG',
] as const;
export type PrepackagedGlutenClaimEvidence = (typeof PREPACKAGED_GLUTEN_CLAIM_EVIDENCES)[number];

// DECIMAL(12,3): at most nine integer digits and three fraction digits.
export const PREPACKAGED_POSITIVE_DECIMAL_PATTERN = /^(?!0+(?:\.0+)?$)\d{1,9}(?:\.\d{1,3})?$/;
export const PREPACKAGED_PERCENT_DECIMAL_PATTERN = /^(?:100(?:\.0{1,2})?|\d{1,2}(?:\.\d{1,2})?)$/;
export const PREPACKAGED_PLAIN_TEXT_PATTERN = /^[^<>]*$/;

export const PREPACKAGED_PROJECT_EDITOR_FIELDS = [
  'legalName',
  'commercialName',
  'osaName',
  'osaAddress',
  'netQuantityG',
  'netQuantityUnit',
  'dateMarkType',
  'dateMarkDate',
  'storageInstructions',
  'usageInstructions',
  'originIndication',
  'primaryIngredientName',
  'primaryIngredientOrigin',
  'primaryIngredientOriginForm',
  'alcoholByVolume',
  'drainedWeightG',
  'quidDeclarations',
  'packagingComponents',
  'packagingPictogramsEnabled',
  'customSections',
  'customLayout',
  'layoutDocument',
  'widthMm',
  'heightMm',
  'establishmentIndication',
  'establishmentAddress',
  'establishmentMarkingDeclared',
  'identificationMarkCountryCode',
  'identificationMarkApprovalNumber',
  'organicControlBodyCode',
  'organicAgricultureOrigin',
  'organicAgricultureOriginCountry',
  'organicCertificateDocumentId',
  // F / G10-G12.
  'netQuantityPresentation',
  'netQuantityUnitCount',
  'netQuantityExcludesGlazing',
  'tareWeightG',
  'eMarkDeclared',
  'eMarkRole',
  'eMarkEvidenceDocumentId',
  // Indicazioni volontarie senza licenza (claim testuali + pittogramma UE
  // bicchiere-forchetta): dichiarazioni dell'operatore, come l'E_MARK.
  'voluntaryMarkGlutenFree',
  'voluntaryMarkVegan',
  'voluntaryMarkFoodContact',
  // E / G8 tranche C: All. VI Parte A punto 4 — indicazione del componente
  // o ingrediente utilizzato in sostituzione parziale o completa di quello
  // che il consumatore presume presente. Testo dell'operatore, stampato in
  // prossimità della denominazione a x-height >= 75%.
  'substituteIndication',
  'weighingInstrumentRef',
  'weighingInstrumentKind',
  'weighingInstrumentVerifiedOn',
  // G15 / G17.
  'frontOfPackRepetition',
  'sellingPrice',
  // Art. 34 §2 lett. c: opt-in per la colonna "per porzione" (macro soltanto),
  // calcolata dalle porzioni della ricetta. Senza porzioni non compare.
  'nutritionPerPortion',
  // Opt-in per etichetta: include nel «può contenere» le categorie di
  // contaminazione crociata dichiarate dal laboratorio. Spento di default.
  'includeTenantContamination',
  // Fase 2 (spec §3.3, D7): tenore minimo di cacao dichiarato, numerico
  // nullable; il compositore lo stampa come menzione «cacao: NN % min».
  'cocoaMinimumPercent',
  // D / G6.
  'verticalOrigin',
  // Modalità libera (spec §4.4): assegnato da GS1 al titolare del marchio, lo
  // scrive l'utente (proprietà BARCODE o form dati) e può essere proposto
  // dall'assistente. Non è gateato da risposte di profilo.
  'gtin',
] as const;

const dateOnlySchema = z.iso.date();
const nullableDateOnlySchema = dateOnlySchema.nullable();
const nullableNumberSchema = z.number().finite().nonnegative().nullable();
const nullableBooleanSchema = z.boolean().nullable();

export const PrepackagedRegulatoryProfileSchema = z.object({
  marketCountries: z.array(PrepackagedMarketCountryCodeSchema).min(1),
  firstMarketDate: nullableDateOnlySchema,
  salesMode: z.enum(PREPACKAGED_SALES_MODES).nullable(),
  channels: z.array(z.enum(PREPACKAGED_SALES_CHANNELS)),
  specialCategories: z.array(z.enum(PREPACKAGED_SPECIAL_CATEGORIES)),
  productKind: z.enum(PREPACKAGED_PRODUCT_KINDS).nullable().default(null).optional(),
  package: z.object({
    largestSurfaceCm2: nullableNumberSchema,
    largestSideAreaCm2: nullableNumberSchema,
    inLiquidMedium: nullableBooleanSchema,
    separableComponents: nullableBooleanSchema,
    /**
     * All. IX punti 3-4 Reg. (UE) 1169/2011: se il preimballaggio è
     * composto da più unità individuali e, se sì, se quelle unità sono
     * unità di vendita (punto 3) o no (punto 4). Nullable con default:
     * profili salvati prima di questa domanda continuano a parsare.
     */
    multipack: z.enum(PREPACKAGED_MULTIPACK_KINDS).nullable().default(null).optional(),
    /**
     * All. IX punto 3, secondo comma, prima condizione: il numero totale
     * degli imballaggi individuali è chiaramente visto e contato
     * dall'esterno. Rilevante solo quando `multipack === 'UNITS_ARE_SALES_UNITS'`.
     */
    multipackPackageCountVisibleFromOutside: nullableBooleanSchema.default(null).optional(),
    /**
     * All. IX punto 3, secondo comma, seconda condizione: almeno
     * un'indicazione della quantità netta di ciascun imballaggio è
     * chiaramente vista dall'esterno. Le due condizioni sono cumulative
     * (`multipackPerUnitDerogationApplies`, `net-quantity-declaration.ts`).
     */
    multipackUnitNetQuantityVisibleFromOutside: nullableBooleanSchema.default(null).optional(),
    /**
     * All. IX punto 5, secondo periodo: l'alimento è presentato con una
     * glassatura. Quando true la quantità netta dichiarata deve essere
     * confermata al netto della glassa (`netQuantityExcludesGlazing`,
     * progetto) — Prospero non calcola la percentuale di glassatura.
     */
    glazed: nullableBooleanSchema.default(null).optional(),
  }),
  characteristics: z.object({
    shelfLife: z.enum(PREPACKAGED_SHELF_LIFE_DECISIONS).nullable(),
    /** All. X punto 1(a): numeric shelf-life duration used to select date precision. */
    shelfLifeDurationMonths: z.number().finite().nonnegative().nullable().default(null).optional(),
    lotIdentification: z
      .enum(PREPACKAGED_LOT_IDENTIFICATION_DECISIONS)
      .nullable()
      .default(null)
      .optional(),
    storageInstructionsNeeded: nullableBooleanSchema,
    usageInstructionsNeeded: nullableBooleanSchema,
    originPresentation: z.enum(PREPACKAGED_ORIGIN_PRESENTATIONS).nullable(),
    primaryIngredientName: z.string().nullable().default(null).optional(),
    primaryIngredientOrigin: z.string().nullable().default(null).optional(),
    primaryIngredientOriginForm: z
      .enum(PREPACKAGED_PRIMARY_INGREDIENT_ORIGIN_FORMS)
      .nullable()
      .default('SPECIFIC')
      .optional(),
    emphasizedIngredientNames: z.array(z.string()),
    alcoholicBeverage: nullableBooleanSchema,
    alcoholByVolume: nullableNumberSchema,
    nutritionExemption: z.enum(PREPACKAGED_NUTRITION_EXEMPTIONS).nullable(),
    hasClaims: nullableBooleanSchema,
    /**
     * Art. 18 §3: an ingredient present as an engineered nanomaterial is
     * followed by the word "nano" in the ingredient list. Nothing in a recipe
     * can tell us — only the operator knows — so the label asks, and an
     * affirmative answer is a specialist's call rather than a field to fill.
     * Optional and defaulting to null, like the lot decision above: profiles
     * saved before the question existed still parse, and simply arrive with it
     * unanswered.
     */
    containsEngineeredNanomaterials: nullableBooleanSchema.default(null).optional(),
    /**
     * All. VI Parte A punto 3 Reg. (UE) 1169/2011: gli alimenti trattati con
     * radiazioni ionizzanti recano «irradiato» o «trattato con radiazioni
     * ionizzanti». Nulla nella ricetta può dirlo — solo l'operatore lo sa —
     * quindi l'etichetta chiede, stesso pattern tri-stato di
     * `containsEngineeredNanomaterials` qui sopra: nullable con default,
     * cosicché i profili salvati prima che la domanda esistesse continuino a
     * validarsi e arrivino semplicemente senza risposta. A differenza dei
     * nanomateriali, una risposta affermativa QUI non manda a
     * `REQUIRES_SPECIALIST_REVIEW`: la scelta fra le due diciture ammesse è
     * già presa una volta per tutte in
     * `prepackaged-denomination-mentions.ts` (decisione 3
     * `prepackaged-ERRATA-2026-08-20.md`), quindi il motore stampa la
     * menzione invece di rimandare a un consulente.
     */
    treatedWithIonisingRadiation: nullableBooleanSchema.default(null).optional(),
    /**
     * E / G8 tranche B: All. VI Parte A punto 6 Reg. (UE) 1169/2011 —
     * l'indicazione dell'acqua aggiunta oltre il 5% del peso finito è dovuta
     * solo per carni, preparazioni di carni e prodotti della pesca presentati
     * come tagli, fette, porzioni, carcasse o filetti. Ambito dichiarato
     * dall'operatore, stesso pattern dei regimi verticali del cluster D:
     * `null` significa «non si applica» e non blocca mai — un profilo salvato
     * prima di questa domanda resta valido e non dovuto. La percentuale
     * d'acqua aggiunta non si chiede: la deriva l'adapter per sottrazione dal
     * peso finito (`addedWaterPercentOfFinished`).
     */
    meatFisheryCutPresentation: z
      .enum(['MEAT_CUT', 'FISHERY_CUT'])
      .nullable()
      .default(null)
      .optional(),
    /**
     * E / G8 tranche B: All. VI Parte A punto 7 — prodotti e preparazioni di
     * carni o prodotti della pesca che sembrano un pezzo unico ma sono l'unione
     * di più parti. `MEAT` stampa «costituito da parti di carne», `FISH`
     * «costituito da parti di pesce» — diciture letterali della norma. `null`
     * = non si applica.
     */
    formedFromMultipleParts: z.enum(['MEAT', 'FISH']).nullable().default(null).optional(),
    /**
     * E / G8 tranche B: All. VI Parte A punto 5 — proteine aggiunte in quanto
     * tali, ivi idrolizzate, di diversa origine animale: la denominazione
     * reca presenza E origine. L'origine è testo libero dell'operatore
     * («manzo», «latte»); senza origine la menzione non si stampa (la norma
     * prescrive entrambe). `null` = nessuna proteina animale aggiunta.
     */
    addedAnimalProteinOrigin: z.string().trim().max(80).nullable().default(null).optional(),
    /**
     * La domanda Sì/No che apre l'origine (V17): la presenza della proteina
     * animale aggiunta lo sa solo l'operatore — il testo libero non è una
     * domanda. `null` = non ancora risposto: l'origine non si apre, salvo
     * che sia già compilata (profili pre-V17, dove resta modificabile);
     * `false` = dichiarato assente, l'origine sparisce e la dipendenza del
     * questionario la svuota.
     */
    hasAddedAnimalProtein: nullableBooleanSchema.default(null).optional(),
    /**
     * E / G8 tranche B: All. VI Parte C — quando un budello per insaccati non
     * è commestibile, la caratteristica va specificata. `true` stampa
     * «budello non commestibile» (forma d'uso corrente: la norma prescrive il
     * contenuto, non la stringa). `null`/`false` = non dovuta.
     */
    nonEdibleCasing: nullableBooleanSchema.default(null).optional(),
    /**
     * E / G9 Parte D: All. VII Parte D punto 3 — la chinina e/o la caffeina
     * usate come aromi sono designate con la denominazione specifica
     * immediatamente dopo il termine «aroma(i)». Il dato non è derivabile da
     * nessun campo: lo dichiara l'operatore. `true` accende la verifica
     * testuale sulle righe aroma (`aromaRowsLackingNamedStimulant`).
     */
    flavouringWithQuinineOrCaffeine: nullableBooleanSchema.default(null).optional(),
    annexIIITriggers: z.preprocess(
      normalizeLegacyAnnexIIITriggers,
      z.array(z.enum(PREPACKAGED_ANNEX_III_TRIGGERS)),
    ),
    annexIIICaffeineMgPerL: nullableNumberSchema.default(null).optional(),
    annexIIICaffeineMatrix: z
      .enum(['BEVERAGE', 'OTHER', 'UNKNOWN'])
      .nullable()
      .default(null)
      .optional(),
    annexIIILiquoricePresent: nullableBooleanSchema.default(null).optional(),
    annexIIILiquoriceAmount: nullableNumberSchema.default(null).optional(),
    annexIIILiquoriceUnit: z
      .enum(['MG_PER_KG', 'G_PER_KG', 'MG_PER_L'])
      .nullable()
      .default(null)
      .optional(),
    annexIIILiquoriceMatrix: z
      .enum(['CONFECTIONERY', 'BEVERAGE', 'OTHER'])
      .nullable()
      .default(null)
      .optional(),
    annexIIILiquoriceAlcoholByVolume: nullableNumberSchema.default(null).optional(),
    annexIIIAspartameDesignation: z
      .enum(['E_NUMBER', 'SPECIFIC_NAME'])
      .nullable()
      .default(null)
      .optional(),
    /**
     * Art. 4 c. 3 lett. b) D.Lgs. 145/2017 + art. 5 §1 Reg. (CE) 853/2004: la
     * domanda unisce due condizioni che la norma tiene separate — prodotto di
     * origine animale dell'allegato III E stabilimento soggetto a
     * riconoscimento — perche' per l'operatore sono un fatto solo: «ho il
     * riconoscimento della ASL o solo la SCIA». Non riusa `specialCategories`
     * perche' spuntare MEAT/FISHERY la' fa scattare
     * IT.SCOPE.SPECIAL_CATEGORY -> REQUIRES_SPECIALIST_REVIEW, bloccante.
     * Nullable con default, come sopra: i profili salvati prima che la
     * domanda esistesse continuano a validarsi.
     */
    animalOriginApprovedEstablishment: nullableBooleanSchema.default(null).optional(),
    /**
     * Art. 7 D.Lgs. 145/2017 (mutuo riconoscimento): il decreto non si
     * applica ai preimballati legalmente fabbricati o commercializzati in un
     * altro Stato membro, in Turchia o in uno Stato EFTA del SEE. Non e' il
     * paese di VENDITA (`marketCountries`, gia' esistente) ma il paese di
     * FABBRICAZIONE. Default `IT` — non `null` come le altre domande — perche'
     * l'assenza di risposta deve restare il caso piu' sicuro: presumere la
     * fabbricazione in Italia, mai un'esenzione non dichiarata.
     */
    manufacturingCountry: PrepackagedMarketCountryCodeSchema.nullable().default('IT').optional(),
    /**
     * Artt. 34-35 Reg. (UE) 2018/848: il titolo è dell'OPERATORE, non del
     * prodotto — l'operatore è notificato e certificato. Nullable con
     * default: i profili salvati prima che la domanda esistesse continuano a
     * validarsi e arrivano semplicemente senza risposta.
     */
    organicCertificationHeld: nullableBooleanSchema.default(null).optional(),
    /**
     * Art. 30 §5: dove può comparire il termine, e di conseguenza se il logo
     * biologico è obbligatorio (lett. a, ≥95% ingredienti agricoli
     * biologici in peso), vietato perché il termine compare solo
     * nell'elenco ingredienti (lett. b/c) o vietato per conversione (§3).
     */
    organicScope: z.enum(PREPACKAGED_ORGANIC_SCOPES).nullable().default(null).optional(),
    /**
     * Allegato, sezione A, Reg. di esecuzione (UE) 828/2014: il tenore
     * analitico dichiarato sull'alimento come venduto — un dato che il
     * modulo non può calcolare dalla ricetta.
     */
    glutenClaimEvidence: z
      .enum(PREPACKAGED_GLUTEN_CLAIM_EVIDENCES)
      .nullable()
      .default(null)
      .optional(),
    /** All. IX punto 1 Reg. (UE) 1169/2011: quale delle tre omissioni si applica, se una si applica. */
    netQuantityExemption: z
      .enum(PREPACKAGED_NET_QUANTITY_EXEMPTION_REASONS)
      .nullable()
      .default(null)
      .optional(),
    /** Lett. b): l'esenzione sotto i 5 g/ml non copre spezie ed erbe aromatiche. */
    netQuantityExemptionIsSpiceOrHerb: nullableBooleanSchema.default(null).optional(),
    /** Lett. c): se il numero dei pezzi è visibile e contabile dall'esterno. */
    netQuantityPieceCountVisibleFromOutside: nullableBooleanSchema.default(null).optional(),
    /**
     * Dir. 2007/45/CE, Allegato punto 1: la categoria di bevanda le cui
     * gamme nominali residue restano obbligatorie. `null` = nessuna
     * categoria vincolata (non entra in `specialCategories`: § 3.5 punto 2
     * del piano — una categoria speciale non vuota manderebbe ogni obbligo
     * non soddisfatto in `REQUIRES_SPECIALIST_REVIEW`).
     */
    nominalRangeCategory: z
      .enum(PREPACKAGED_NOMINAL_RANGE_CATEGORIES)
      .nullable()
      .default(null)
      .optional(),
    /**
     * D / G6 (storico): quali categorie merceologiche verticali (carni
     * bovine e fresche, prodotti della pesca, miele, olio d'oliva, i cinque
     * regimi sperimentali italiani) impongono un'origine propria oltre a
     * quella generale dell'art. 26. Dal V12 (2026-08-22) questa domanda NON
     * è più raccolta dal form: i regimi si deducono dagli entry
     * `verticalOrigin` delle schede ingrediente. Il campo resta nel tipo
     * wire (tolleranza verso i profili salvati prima della migrazione) e
     * l'evaluator non lo legge più.
     */
    verticalOriginRegimes: z.array(z.enum(PREPACKAGED_VERTICAL_ORIGIN_REGIMES)).optional(),
    // Fase 2 (spec §3.1): i quattro campi pasticceria, stesso pattern V13.
    thermalState: z.enum(PREPACKAGED_THERMAL_STATES).nullable().default(null).optional(),
    vanillaFlavouringKind: z
      .enum(PREPACKAGED_VANILLA_FLAVOURING_KINDS)
      .nullable()
      .default(null)
      .optional(),
    artisanRegistered: nullableBooleanSchema.default(null).optional(),
    reservedBakeryMinimaVerified: nullableBooleanSchema.default(null).optional(),
  }),
});

export type PrepackagedRegulatoryProfile = z.infer<typeof PrepackagedRegulatoryProfileSchema>;
export type PrepackagedChecklistStatus = (typeof PREPACKAGED_CHECKLIST_STATUSES)[number];
export type PrepackagedSalesMode = (typeof PREPACKAGED_SALES_MODES)[number];
export type PrepackagedSpecialCategory = (typeof PREPACKAGED_SPECIAL_CATEGORIES)[number];
export type PrepackagedSalesChannel = (typeof PREPACKAGED_SALES_CHANNELS)[number];
export type PrepackagedShelfLifeDecision = (typeof PREPACKAGED_SHELF_LIFE_DECISIONS)[number];
export type PrepackagedLotIdentificationDecision =
  (typeof PREPACKAGED_LOT_IDENTIFICATION_DECISIONS)[number];
export type PrepackagedOriginPresentation = (typeof PREPACKAGED_ORIGIN_PRESENTATIONS)[number];
export type PrepackagedNutritionExemption = (typeof PREPACKAGED_NUTRITION_EXEMPTIONS)[number];
export type PrepackagedAnnexIIITrigger = (typeof PREPACKAGED_ANNEX_III_TRIGGERS)[number];
export type PrepackagedProfileFieldPath = (typeof PREPACKAGED_PROFILE_FIELD_PATHS)[number];
export type PrepackagedProjectEditorField = (typeof PREPACKAGED_PROJECT_EDITOR_FIELDS)[number];
export type { PrepackagedNominalRangeCategory };

export const PrepackagedFactsContextSchema = z.enum(['TEMPLATE', 'GENERATION']);
/**
 * One ingredient standing between the label and a satisfied obligation, with
 * the tab of its own sheet where that fact is actually edited: per-100g values
 * live on `NUTRITION`, while allergens and the physical factors (edible
 * portion, density, piece weight) live on `DATA`. Carrying the tab per
 * ingredient — rather than fixing it per rule — is what lets one rule send the
 * user to whichever tab really holds the missing datum.
 *
 * `name` is here so the CTA can say WHICH of a dozen ingredients to open; it
 * must never be used as an identifier. `ingredientId` is the only id, and it
 * is `IdentifierSchema`-validated because it becomes the `ingredientId` of an
 * `INGREDIENT` editor target inside an evaluation that is `safeParse`d as a
 * whole: one malformed id would drop the entire checklist.
 */
/**
 * D6 (decisione 2026-08-21): una riga dell'elenco ingredienti la cui
 * designazione generica «carni di ...» (All. VII Parte B punto 17) non
 * risulta ammessa — `evaluateGenericMeatCategoryDesignation`
 * (`annex-vii-designations.ts`) è la sola fonte di `kind`, mai un secondo
 * giudizio derivato qui: `EXCEEDS_THRESHOLD` quando il tenore è noto e
 * supera la soglia (un'unica via d'uscita, la denominazione specifica) o
 * `UNKNOWN_CONTENT` quando il tenore non è mai stato misurato (due vie
 * aperte: la denominazione specifica, o compilare grassi e collagene sulla
 * scheda ingrediente). `detail` porta la frase italiana già composta
 * dall'adapter per il messaggio bloccante esistente; `kind` è quello che
 * permette al frontend di scegliere il testo giusto senza indovinarlo da
 * `detail`.
 */
export const GenericMeatCategoryRowSchema = z.object({
  ingredientId: IdentifierSchema,
  ingredientName: z.string().min(1).max(300),
  kind: z.enum(['EXCEEDS_THRESHOLD', 'UNKNOWN_CONTENT']),
  detail: z.string().min(1),
});
export type GenericMeatCategoryRow = z.infer<typeof GenericMeatCategoryRowSchema>;

/**
 * DEV-283: quale dato della scheda ingrediente blocca la dichiarazione
 * nutrizionale. Senza, il task diceva solo "mancano i dati" e l'operatore
 * compilava i valori nutrizionali mentre mancava la densità del latte in ml.
 * `DENSITY`/`PIECE_WEIGHT` convertono ml/pezzi in grammi (in alternativa il
 * «Peso in grammi» della riga ricetta); `EDIBLE_PORTION` blocca solo negli
 * snapshot che lo portano ancora come ERROR.
 */
export const PREPACKAGED_INGREDIENT_MISSING_FACTS = [
  'NUTRITION_PROFILE',
  'DENSITY',
  'PIECE_WEIGHT',
  'EDIBLE_PORTION',
] as const;
export type PrepackagedIngredientMissingFact =
  (typeof PREPACKAGED_INGREDIENT_MISSING_FACTS)[number];

export const PrepackagedMissingIngredientSchema = z.object({
  ingredientId: IdentifierSchema,
  name: z.string().min(1).max(300),
  tab: z.enum(['DATA', 'NUTRITION']),
  /** Solo per la nutrizione (gli allergeni non lo portano); opzionale perché
   *  le readiness degli altri controlli non hanno un dato da nominare. */
  missingFact: z.enum(PREPACKAGED_INGREDIENT_MISSING_FACTS).optional(),
});

export const PrepackagedReadinessSchema = z.object({
  ready: z.boolean(),
  missingCount: z.number().int().nonnegative().optional(),
  /** Plain id projection of `missingIngredients`, for consumers that only need identity. */
  missingKeys: z.array(z.string()).optional(),
  missingIngredients: z.array(PrepackagedMissingIngredientSchema).optional(),
  /** Quante righe ingrediente ha la ricetta in tutto: senza il totale, "2 da
   *  completare" non dice se manca poco o quasi tutto. */
  totalIngredientCount: z.number().int().nonnegative().optional(),
  /**
   * Perché il calcolo è bloccato quando NESSUN ingrediente può essere
   * nominato: peso finito implausibile, porzioni mancanti, riga non
   * collegata, sotto-ricetta irrisolta. Sono codici di
   * `RECIPE_NUTRITION_ISSUE_CODES`, tenuti qui come stringhe perché questo
   * contratto non deve dipendere dall'enum del calcolo nutrizionale; senza di
   * loro il task può solo aprire il pannello ricetta e tacere, che è
   * esattamente il difetto che questo campo esiste per chiudere.
   */
  recipeBlockerCodes: z.array(z.string().min(1).max(64)).optional(),
});
export const PrepackagedPackagingComponentSchema = z.object({
  name: z.string(),
  materialCode: z.string(),
  disposalStream: z.enum(PACKAGING_DISPOSAL_STREAMS),
});

/**
 * The ONE normalization for matching an ingredient name between the
 * emphasized-ingredients profile answer and the QUID declaration rows. The
 * backend rule engine (`quidIsComplete`, prepackaged-regulatory.evaluator.ts)
 * and the frontend section sync (quid-sync.ts) both match through this
 * function: keeping two local copies is exactly how «vino rosso» ended up
 * emphasized in the profile with its declaration row invisible to the rule.
 */
export function normalizePrepackagedIngredientName(value: string): string {
  // `replace` with a global regex, not `replaceAll`: shared compiles below
  // es2021 and must stay loadable by every workspace.
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase('it-IT');
}

export function parsePrepackagedQuidPercentage(value: string): number | null {
  const trimmed = value.trim();
  if (!/^(?:\d+(?:[.,]\d+)?|[.,]\d+)\s*%?$/.test(trimmed)) {
    return null;
  }
  const numberValue = Number(trimmed.replace(/%\s*$/, '').replace(',', '.'));
  return Number.isFinite(numberValue) && numberValue > 0 && numberValue <= 100 ? numberValue : null;
}

export const PrepackagedQuidDeclarationSchema = z
  .object({
    ingredient: z.string(),
    percentage: z.string(),
  })
  .superRefine((declaration, ctx) => {
    if (parsePrepackagedQuidPercentage(declaration.percentage) === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['percentage'],
        message: 'percentage must be a finite decimal greater than 0 and no greater than 100',
      });
    }
  });
export const PrepackagedCustomSectionSchema = z.object({
  key: z.string(),
  title: z.string(),
  text: z.string(),
  printable: z.boolean(),
});

export const PrepackagedRegulatoryFactsSchema = z
  .object({
    context: PrepackagedFactsContextSchema,
    layoutMode: z.enum(['STANDARD', 'CUSTOM']),
    smallPackageDerogation: z.boolean(),
    legalName: z.string().nullable(),
    commercialName: z.string().nullable(),
    osaName: z.string().nullable(),
    osaAddress: z.string().nullable(),
    netQuantityG: z.string().nullable(),
    /**
     * L'unità in cui la quantità netta è dichiarata. `netQuantityG` porta il solo
     * numero: senza questa non si può né applicare una tabella di tolleranze né
     * confrontare con una gamma nominale in millilitri. Nullable con default
     * perché i fatti congelati prima che il campo esistesse devono continuare a
     * parsare, e vanno giudicati sui dati che avevano.
     */
    netQuantityUnit: z.enum(PREPACKAGED_NET_QUANTITY_UNITS).nullable().default(null),
    dateMarkType: z.enum(['BEST_BEFORE', 'USE_BY']).nullable(),
    dateMarkExempt: z.boolean().nullable(),
    storageInstructions: z.string().nullable(),
    usageInstructions: z.string().nullable(),
    originIndication: z.string().nullable(),
    primaryIngredientName: z.string().nullable().default(null).optional(),
    primaryIngredientOrigin: z.string().nullable().default(null).optional(),
    primaryIngredientOriginForm: z
      .enum(PREPACKAGED_PRIMARY_INGREDIENT_ORIGIN_FORMS)
      .nullable()
      .default('SPECIFIC')
      .optional(),
    alcoholByVolume: z.string().nullable(),
    drainedWeightG: z.string().nullable(),
    quidDeclarations: z.array(PrepackagedQuidDeclarationSchema),
    packagingComponents: z.array(PrepackagedPackagingComponentSchema),
    customSections: z.array(PrepackagedCustomSectionSchema),
    /**
     * Which particulars the label actually shows, and where on it. Two rules
     * are about placement and not about data — art. 13 §5 groups three
     * particulars in the same field of vision, All. VIII punto 3 lett. b) puts
     * the QUID percentage next to the denomination or in the ingredient list —
     * and without this the checklist attested placements it had never looked
     * at. Null while there is no usable layout; defaults to null so facts
     * frozen before this field existed still parse, and are judged on their
     * data alone as they were.
     */
    visibleLayoutBlocks: z
      .array(
        z.object({
          id: z.string(),
          xMm: z.number(),
          yMm: z.number(),
          widthMm: z.number(),
          heightMm: z.number(),
          /**
           * The face of the package this block sits on, derived from its
           * geometry. `null` covers two distinct cases a rule must treat the
           * same way — no face model on this document, or the block
           * straddles a fold — and in both the conservative answer is "not
           * provably on the principal face". Nullable with default so facts
           * frozen before this field existed still parse.
           */
          faceId: z.string().nullable().default(null),
          /**
           * Il corpo del carattere del blocco, in mm, quando lo stile del
           * blocco lo dichiara esplicitamente (`typography.fontSizeMm` sul
           * blocco V2). Assente quando il blocco eredita il corpo dal
           * documento — in quel caso la regola tipografica legge
           * `presetManifest.typography.baseFontSizeMm`. Serve alle due
           * regole di altezza minima dir. 76/211/CEE (quantità nominale
           * punto 3.1, marchio «℮» punto 3.3): senza questo campo non c'è
           * modo di verificare quanto è alto ciò che è già stampato.
           */
          fontSizeMm: z.number().finite().positive().optional(),
        }),
      )
      .nullable()
      .default(null),
    declaredMicronutrientCodes: z.array(z.enum(MICRONUTRIENT_CODES)),
    /**
     * All. VIII punto 3 lett. b) Reg. (UE) 1169/2011: il QUID è dovuto anche
     * quando un ingrediente della ricetta compare nella denominazione
     * dell'alimento, non solo quando l'operatore lo evidenzia a mano. Nomi
     * grezzi degli ingredienti della ricetta congelata, nell'ordine della
     * ricetta; mai un identificatore, solo testo per il confronto con
     * `legalName`/`commercialName`. Default vuoto così i fatti congelati
     * prima che il campo esistesse continuano a parsare.
     */
    recipeIngredientNames: z.array(z.string()).default([]),
    /** Art. 18 §3: true when composed ingredient lines contain `(nano)`. */
    engineeredNanomaterialDeclarationReady: z.boolean().optional(),
    requiresStorageInstructions: z.boolean(),
    requiresUsageInstructions: z.boolean(),
    requiresOriginIndication: z.boolean(),
    requiresAlcoholDeclaration: z.boolean(),
    requiresDrainedWeight: z.boolean(),
    requiresQuid: z.boolean(),
    requiresPackagingDisposal: z.boolean(),
    widthMm: z.number().finite().positive(),
    heightMm: z.number().finite().positive(),
    ingredientReadiness: PrepackagedReadinessSchema,
    allergenReadiness: PrepackagedReadinessSchema,
    nutritionReadiness: PrepackagedReadinessSchema,
    packagingComponentCount: z.number().int().nonnegative(),
    lotNumber: z.string().nullable(),
    dateMarkDate: nullableDateOnlySchema,
    /** B / G3-G5. Sede dello stabilimento e marchio di identificazione: campi
     *  di progetto, non del profilo (a differenza di
     *  `animalOriginApprovedEstablishment`, che è una scelta legale). Default
     *  null così i fatti congelati prima che esistessero continuano a
     *  parsare. */
    establishmentIndication: z.enum(PREPACKAGED_ESTABLISHMENT_INDICATIONS).nullable().default(null),
    establishmentAddress: z.string().nullable().default(null),
    establishmentMarkingDeclared: z.boolean().default(false),
    identificationMarkCountryCode: z.string().nullable().default(null),
    identificationMarkApprovalNumber: z.string().nullable().default(null),
    /** Art. 32 §1 lett. a): codice dell'organismo di controllo. Dato del
     *  progetto (non del tenant, decisione 19 agosto 2026), null finché non
     *  dichiarato. Default null così i fatti congelati prima che il campo
     *  esistesse continuano a parsare. */
    organicControlBodyCode: z.string().nullable().default(null),
    organicAgricultureOrigin: z.enum(PREPACKAGED_ORGANIC_ORIGINS).nullable().default(null),
    organicAgricultureOriginCountry: z.string().nullable().default(null),
    /**
     * F / G10-G12. Tutti default-friendly: i fatti congelati prima di questo
     * cluster continuano a parsare come un preimballaggio singolo (§ 5.6 del
     * piano cluster F).
     */
    netQuantityPresentation: z.enum(PREPACKAGED_NET_QUANTITY_PRESENTATIONS).default('SINGLE'),
    netQuantityUnitCount: z.number().int().positive().nullable().default(null),
    /** All. IX punto 5, secondo periodo: confermato al netto della glassatura. */
    netQuantityExcludesGlazing: z.boolean().nullable().default(null),
    /** Tara dichiarata, mai stampata: solo per l'avviso di plausibilità. */
    tareWeightG: z.string().nullable().default(null),
    /** Dir. 76/211/CEE All. I punto 3.3: dichiarazione dell'operatore, mai un verdetto del software. */
    eMarkDeclared: z.boolean().default(false),
    eMarkRole: z.enum(PREPACKAGED_EMARK_ROLES).nullable().default(null),
    /** Indicazioni volontarie SENZA licenza dichiarate nello step Dati
     *  (claim testuali + pittogramma UE bicchiere-forchetta, pubblico
     *  dominio). I marchi licenziati (spiga AIC, V-Label, Halal, RESY,
     *  Punto Verde, FSC) sono deliberatamente fuori catalogo. */
    voluntaryMarks: z
      .object({
        SENZA_GLUTINE: z.boolean().optional(),
        VEGAN: z.boolean().optional(),
        BICCHIERE_FORCHETTA: z.boolean().optional(),
      })
      .optional(),
    weighingInstrumentRef: z.string().nullable().default(null),
    weighingInstrumentKind: z.enum(PREPACKAGED_WEIGHING_INSTRUMENT_KINDS).nullable().default(null),
    weighingInstrumentVerifiedOn: nullableDateOnlySchema.default(null),
    /**
     * D.M. 93/2017: se la verificazione periodica dello strumento è scaduta,
     * calcolato dall'adapter (che ha accesso al contesto/data corrente) e
     * mai da `new Date()` dentro l'evaluator, che resta puro e deterministico
     * (piano cluster F § 7.3). `null` quando la scadenza non è calcolabile
     * (nessuno strumento dichiarato).
     */
    weighingInstrumentExpired: z.boolean().nullable().default(null),
    /** Dir. 2007/45/CE: se la categoria di bevanda vincola una gamma nominale. */
    requiresNominalRange: z.boolean().default(false),
    /** Dir. 76/211/CEE: se il marchio «℮» è dichiarato e i suoi presupposti vanno verificati. */
    requiresEMarkPrerequisites: z.boolean().default(false),
    /**
     * E / G8: All. VI Parte A punto 2 — vero quando la ricetta è venduta
     * decongelata (`Recipe.servedDefrosted`, derivato e scritto una sola
     * volta da `resolveDefrostState`, mai da questo modulo). `null` di
     * default così i fatti congelati prima che il campo esistesse
     * continuano a parsare, giudicati come se la menzione non fosse dovuta —
     * la stessa lettura che `resolveDefrostState` dà a una ricetta mai
     * passata dal cartello.
     */
    servedDefrosted: z.boolean().nullable().default(null),
    /**
     * E / G8 tranche B: All. VI Parte A punto 6 — percentuale di acqua
     * aggiunta sul peso del prodotto finito, calcolata per sottrazione
     * (All. VII Parte A punto 1): peso finito meno la somma degli altri
     * ingredienti, diviso il peso finito. Derivata dall'adapter dallo stesso
     * snapshot del lotto, mai chiesta. `null` quando il peso finito non è
     * noto — in quel caso la composizione è già bloccata altrove
     * (`nutrition.finishedWeightG`) e la menzione non è decidibile.
     */
    addedWaterPercentOfFinished: z.number().nullable().default(null),
    /**
     * E / G8 tranche C: All. VI Parte A punto 4 — testo dichiarato
     * dall'operatore che indica il componente o ingrediente utilizzato in
     * sostituzione parziale o completa di quello che il consumatore presume
     * presente. `null` = nessuna sostituzione dichiarata, menzione non
     * dovuta. Default null per i fatti congelati prima del V13.
     */
    substituteIndication: z.string().nullable().default(null),
    /**
     * E / G9 Parte D: All. VII Parte D punto 3 — vero quando l'operatore ha
     * dichiarato chinina/caffeina come aromi nel profilo
     * (`characteristics.flavouringWithQuinineOrCaffeine`) E la verifica
     * sull'elenco non è risolta: o una riga aroma non porta la denominazione
     * specifica, o non esiste nessuna riga aroma che la porti. Derivato
     * dall'adapter (fail-closed), mai riderivato dall'evaluator.
     */
    stimulantFlavouringUnresolved: z.boolean().default(false),
    /**
     * E / G9 Parte D: nomi delle righe dell'elenco ingredienti la cui
     * designazione di aroma non porta la denominazione specifica della
     * chinina/caffeina dichiarata. Calcolato dall'adapter con
     * `findAromaRowsLackingNamedStimulant` SOLO quando il profilo dichiara
     * l'uso. Default vuoto.
     */
    aromaRowsLackingNamedStimulant: z.array(z.string()).default([]),
    /**
     * E / G9: All. VII Parte A punti 8-9 — nomi delle righe dell'elenco
     * ingredienti la cui designazione usa «oli vegetali»/«grassi vegetali»
     * (o l'idrogenazione) senza l'origine specifica o il qualificatore che
     * la norma richiede. Il lessico è puro
     * (`shared/src/label/annex-vii-designations.ts`); l'elenco è calcolato
     * dall'adapter leggendo `LotEffectiveLabelData`, mai qui. Default vuoto
     * così i fatti congelati prima che il campo esistesse continuano a
     * parsare come "nessuna riga difettosa".
     */
    nonSpecificVegetableOilRows: z.array(z.string()).default([]),
    /**
     * E fase C: All. VII Parte B punto 17 — righe dell'elenco ingredienti
     * la cui designazione generica «carni di ...» non risulta ammessa: o il
     * tenore di grassi/collagene non è noto, o supera i limiti della
     * tabella. `evaluateGenericMeatCategoryDesignation`
     * (`annex-vii-designations.ts`) decide `kind`, l'adapter legge la
     * composizione reale dal repository e nomina qui solo le righe che
     * falliscono — mai `REQUIRES_SPECIALIST_REVIEW` a tempo indeterminato
     * (decisione 3, `prepackaged-ERRATA-2026-08-20.md`). Struttura estesa
     * da un semplice `string[]` a `GenericMeatCategoryRowSchema[]`
     * (decisione 2026-08-21): la via d'uscita principale è ora la
     * denominazione specifica, non il dato nutrizionale, e il frontend
     * deve poter distinguere «tenore noto e sopra soglia» (un'unica via)
     * da «tenore ignoto» (due vie aperte) senza indovinarlo da una
     * stringa. Default vuoto così i fatti congelati prima che il campo
     * esistesse continuano a parsare come "nessuna riga difettosa".
     */
    genericMeatCategoryRows: z.array(GenericMeatCategoryRowSchema).default([]),
    /**
     * D / G6: le voci di origine verticale che il progetto ha effettivamente
     * scritto (una per regime dichiarato in
     * `regulatoryProfile.characteristics.verticalOriginRegimes`), lette dal
     * campo `verticalOrigin` del progetto. Forma lasca (`Record<string,
     * unknown>`, come in `prepackaged-vertical-origin.ts`, mai un secondo
     * schema Zod discriminato che dovrebbe restare sincrono col primo):
     * `verticalOriginMissingFields`/`evaluatePrepackagedVerticalOrigin`
     * restano l'unica autorità sulla completezza di ciascuna voce. Default
     * vuoto così i fatti congelati prima che il campo esistesse continuano a
     * parsare, giudicati come nessuna voce scritta — corretto, perché prima
     * di questo cluster nessuna voce poteva esistere.
     */
    verticalOrigin: z.array(z.record(z.string(), z.unknown())).default([]),
    /** Derivato da `deriveRegulatoryApplicability` insieme agli altri `requires*`. */
    requiresVerticalOrigin: z.boolean().default(false),
    /**
     * L'id della faccia PRINCIPALE del documento di layout corrente
     * (`principalFaceId`, `prepackaged-label-faces.ts`), o `null` quando non
     * c'è un layout o nessuna faccia dichiara il ruolo PRINCIPAL. Serve solo
     * a chi impone il campo visivo principale (miele, salumi trasformati):
     * confrontato con `visibleLayoutBlocks[].faceId`, mai un secondo
     * derivatore della faccia — la geometria resta l'unica fonte
     * (`faceIdOfGeometry`).
     */
    principalFaceId: z.string().nullable().default(null),
    /**
     * Data di valutazione in forma `YYYY-MM-DD`, passata dal chiamante
     * dell'adapter (mai `new Date()` qui, il che manterrebbe l'evaluator
     * impuro). Serve alla sola scadenza dei cinque regimi sperimentali
     * italiani (`IT_EXPERIMENTAL_ORIGIN_DECREES`): `null` — il default —
     * lascia la regola comportarsi come se la scadenza non fosse mai
     * verificata, cosicché nessuna valutazione storica cambi verdetto.
     */
    evaluationDate: nullableDateOnlySchema.default(null),
    /**
     * Fase 2 (spec §7.1): fatti compositivi e ausiliari del pack pasticceria.
     * Ogni «il testo contiene…» e ogni numero derivato è calcolato
     * dall'adapter (`compositionFactsFrom`) con il lessico deterministico
     * `composition-roles-lexicon.ts`: la DSL non fa aritmetica né parsing di
     * testo. Tutti i campi hanno `.default(...)`: i fatti congelati prima
     * che esistessero continuano a parsare, giudicati come ricetta senza
     * ruoli risolti — lo stesso pattino dei cluster E/D.
     */
    compositionRows: z
      .array(
        z.object({
          name: z.string(),
          role: z.enum(COMPOSITION_ROLES),
          sharePctOfDough: z.number().nullable(),
          sharePctOfFinished: z.number().nullable(),
          isCompound: z.boolean(),
          componentRoles: z.array(z.enum(COMPOSITION_ROLES)),
        }),
      )
      .default([]),
    compositionNameRoles: z.array(z.enum(COMPOSITION_NAME_ROLES)).default([]),
    compositionNameHeadRole: z.enum(COMPOSITION_NAME_ROLES).nullable().default(null),
    /** Ogni riga ha snapshot e peso > 0: solo allora le quote sono giudicabili. */
    compositionComplete: z.boolean().default(false),
    /** `Recipe.servedDefrosted` grezzo, per il cross-check di R5 riga 2: la
     *  controparte OR-ata con lo stato termico è `servedDefrosted` qui sopra. */
    recipeServedDefrosted: z.boolean().default(false),
    /**
     * «Cacao: … % min» dichiarato dall'operatore (art. 5 c.4 D.Lgs. 178/2003),
     * project field esposto come fatto: letto dall'adapter dal config, mai
     * riderivato dall'evaluator. `null` = non dichiarato.
     */
    cocoaMinimumPercent: z.number().nullable().default(null),
    // Fatti ausiliari D.M. 22/7/2005 (coordinato con D.M. 16/5/2017): la
    // denominazione riservata e le sue deroghe, come fatti piatti.
    dm2005NameDeclaresAbsenceRaisin: z.boolean().default(false),
    dm2005NameDeclaresAbsenceCandiedPeel: z.boolean().default(false),
    dm2005NameDeclaresAbsenceGlaze: z.boolean().default(false),
    /** Il nome dichiara «senza glutine»: esonera WHEAT_FLOUR (art. 8-bis, Reg. (UE) 828/2014). */
    dm2005NameLikelyGlutenFree: z.boolean().default(false),
    /**
     * Ruoli obbligatori della tabella spec §7.4 mancanti per la testa
     * D.M. 2005, al netto delle deroghe «senza …» e dell'esonero senza
     * glutine; vuoto quando la testa non è una denominazione riservata.
     */
    dm2005MandatoryRolesMissing: z.array(z.enum(COMPOSITION_ROLES)).default([]),
    dm2005DoughFatOtherThanButter: z.boolean().default(false),
    dm2005LeaveningWithoutSourdough: z.boolean().default(false),
    dm2005BrewersYeastSharePct: z.number().nullable().default(null),
    dm2005SavoiardoEggSharePct: z.number().nullable().default(null),
    dm2005AlmondOrKernelSharePct: z.number().nullable().default(null),
    dm2005ColombaAlmondSharePctOfFinished: z.number().nullable().default(null),
    dm2005QuidDeclaredForEgg: z.boolean().default(false),
    dm2005QuidDeclaredForAlmondOrKernel: z.boolean().default(false),
    dm2005HasCharacterisingAdditions: z.boolean().default(false),
    dm2005NameMentionsAdditions: z.boolean().default(false),
    // Fatti cioccolato (D.Lgs. 178/2003).
    chocolateNonCocoaVegetableFatPresent: z.boolean().default(false),
    chocolateOtherFatsMentionRequired: z.boolean().default(false),
    /** Testa «cioccolato bianco»/«cacao in polvere»/«cacao magro»/«cioccolato in polvere»: art. 5 c.4 non richiede «cacao: … % min». */
    chocolateHeadExemptFromCocoaMinimum: z.boolean().default(false),
    // Fatti vaniglia (Reg. (CE) 1334/2008 art. 16) e conservazione surgelato
    // (D.Lgs. 110/1992 art. 8 c.1), letti dalle righe e dai testi stampati.
    vanillaLineSaysNatural: z.boolean().default(false),
    vanillaLineSaysVanilla: z.boolean().default(false),
    vanillaLineSaysNaturalVanilla: z.boolean().default(false),
    vanillaPodPresent: z.boolean().default(false),
    frozenStorageHasNoRefreezeWarning: z.boolean().default(false),
    frozenStorageHasTemperatureOrEquipment: z.boolean().default(false),
    frozenStorageHasConsumerStoragePeriod: z.boolean().default(false),
    /** Termine «artigian*» nei claim e nelle sezioni custom stampabili (L. 443/1985 art. 5 c.8). */
    artisanTermInClaimsOrSections: z.boolean().default(false),
    // Fatti marmellata (D.Lgs. 50/2004 All. I: «marmellata» riservata agli agrumi).
    jamFruitIsCitrus: z.boolean().default(false),
    jamFruitNamed: z.boolean().default(false),
  })
  .superRefine((facts, ctx) => {
    if (facts.context === 'TEMPLATE' && (facts.lotNumber !== null || facts.dateMarkDate !== null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['lotNumber'],
        message: 'lotNumber and dateMarkDate are generation-only',
      });
    }
  });

export type PrepackagedFactsContext = z.infer<typeof PrepackagedFactsContextSchema>;
export type PrepackagedMissingIngredient = z.infer<typeof PrepackagedMissingIngredientSchema>;
export type PrepackagedReadiness = z.infer<typeof PrepackagedReadinessSchema>;
export type PrepackagedPackagingComponent = z.infer<typeof PrepackagedPackagingComponentSchema>;
export type PrepackagedRegulatoryFacts = z.infer<typeof PrepackagedRegulatoryFactsSchema>;

export const PrepackagedLegalSourceSchema = z.object({
  act: z.string().min(1),
  article: z.string().min(1),
  annex: z.string().min(1).nullable(),
  url: z.url().refine((url) => url.startsWith('https://'), 'legal source must use HTTPS'),
});

export const PrepackagedChecklistEditorTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('PROFILE_FIELD'), path: z.enum(PREPACKAGED_PROFILE_FIELD_PATHS) }),
  z.object({ kind: z.literal('PROJECT_FIELD'), field: z.enum(PREPACKAGED_PROJECT_EDITOR_FIELDS) }),
  z.object({ kind: z.literal('RECIPE_INGREDIENTS') }),
  z.object({
    kind: z.literal('INGREDIENT'),
    ingredientId: IdentifierSchema.nullable(),
    tab: z.enum(['DATA', 'NUTRITION']),
    /** Il campo da raggiungere nella scheda (DEV-283): il CTA lo nomina e la
     *  scheda ci scorre sopra, invece di aprirsi in cima su Dati. */
    missingFact: z.enum(PREPACKAGED_INGREDIENT_MISSING_FACTS).optional(),
  }),
  z.object({ kind: z.literal('NONE') }),
]);

export type PrepackagedLegalSource = z.infer<typeof PrepackagedLegalSourceSchema>;
export type PrepackagedChecklistEditorTarget = z.infer<
  typeof PrepackagedChecklistEditorTargetSchema
>;

const messageParamsSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);

export const PrepackagedChecklistTaskSchema = z
  .object({
    ruleId: z.string().min(1),
    status: z.enum(PREPACKAGED_CHECKLIST_STATUSES),
    titleKey: z.string().min(1),
    descriptionKey: z.string().min(1),
    exampleKey: z.string().min(1).nullable(),
    nonApplicabilityReasonKey: z.string().min(1).nullable(),
    legalSources: z.array(PrepackagedLegalSourceSchema),
    editorTarget: PrepackagedChecklistEditorTargetSchema,
    messageParams: messageParamsSchema.optional(),
  })
  .superRefine((task, ctx) => {
    if (task.status !== 'SUGGESTION' && task.legalSources.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['legalSources'],
        message: 'non-suggestion tasks require an official HTTPS legal source',
      });
    }
  });

export type PrepackagedChecklistTask = z.infer<typeof PrepackagedChecklistTaskSchema>;

/**
 * Spec 2026-09-06-label-free-mode-editor §7.4: rilievo informativo che
 * accompagna (non sostituisce) i task della checklist. `messageKey` è la
 * chiave i18n (`prepackagedLabel.advisories.<CODE>`); `params`,
 * `legalSourceUrl` e `targetId` entrano solo quando il produttore li conosce
 * — mai chiavi con valore `undefined` nel contratto.
 */
export const PrepackagedAdvisorySchema = z.object({
  code: z.string().min(1),
  messageKey: z.string().min(1),
  params: messageParamsSchema.optional(),
  legalSourceUrl: z.string().optional(),
  targetId: z.string().optional(),
});
export type PrepackagedAdvisory = z.infer<typeof PrepackagedAdvisorySchema>;

export const PrepackagedRegulatoryEvaluationSchema = z
  .object({
    rulePackVersion: z.string().min(1),
    rulePackHash: z.string().min(1),
    profileHash: z.string().min(1),
    tasks: z.array(PrepackagedChecklistTaskSchema),
    blockingCount: z.number().int().nonnegative(),
    canContinue: z.boolean(),
    canPrint: z.boolean(),
    /**
     * Spec §6.3: modalità dell'editor con cui l'evaluation è stata prodotta.
     * Opzionale senza default: un'evaluation senza `editorMode` resta una
     * evaluation GUIDED byte-per-byte com'era prima del campo.
     */
    editorMode: z.enum(PREPACKAGED_LABEL_EDITOR_MODES).optional(),
    /** Spec §7.4: rilievi informativi, opzionali e senza default. */
    advisories: z.array(PrepackagedAdvisorySchema).optional(),
  })
  .superRefine((evaluation, ctx) => {
    if (evaluation.editorMode === 'FREE') {
      // Spec §6.3: in FREE la relax azzera i bloccanti (i task restano
      // visibili con il loro stato, l'UI li rende informativi). L'unica
      // eccezione è un rilievo che resta bloccante (STALE_DERIVED_SNAPSHOT
      // con NUTRITION visibile): lì `canPrint` è false e i bloccanti
      // dichiarati restano quelli contati sui task.
      const expectedBlockingCount = evaluation.canPrint
        ? 0
        : evaluation.tasks.filter((task) => isBlockingChecklistStatus(task.status)).length;
      if (evaluation.blockingCount !== expectedBlockingCount) {
        ctx.addIssue({
          code: 'custom',
          path: ['blockingCount'],
          message: `blockingCount must equal ${expectedBlockingCount} in FREE editor mode`,
        });
      }
      return;
    }
    const expectedBlockingCount = evaluation.tasks.filter((task) =>
      isBlockingChecklistStatus(task.status),
    ).length;
    if (evaluation.blockingCount !== expectedBlockingCount) {
      ctx.addIssue({
        code: 'custom',
        path: ['blockingCount'],
        message: `blockingCount must equal ${expectedBlockingCount}`,
      });
    }
  });

export type PrepackagedRegulatoryEvaluation = z.infer<typeof PrepackagedRegulatoryEvaluationSchema>;

export const isBlockingChecklistStatus = (status: PrepackagedChecklistStatus | string): boolean =>
  status === 'TO_COMPLETE' || status === 'NEEDS_ANSWER' || status === 'REQUIRES_SPECIALIST_REVIEW';
